import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login, glogin } from "../../Api/Api";
import { signInWithGoogle } from "../../firebase";
import { FcGoogle } from "react-icons/fc";
import { FaEnvelope, FaLock } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import authHeroImg from "../../Assets/og_technician_real.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      toast.error("Please enter email/mobile and password");
      return;
    }

    setLoading(true);
    try {
      const response = await login({ identifier, password });
      localStorage.setItem("user", JSON.stringify(response.user));
      localStorage.setItem("authToken", response.token);

      toast.success("Login successful! Redirecting...", { autoClose: 1500 });
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error) {
      console.error("Login error:", error);
      const msg = error?.response?.data?.error || "Invalid credentials. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithGoogle();
      const response = await glogin({
        g_id: result.user.uid,
        email: result.user.email,
        name: result.user.displayName,
      });

      localStorage.setItem("authToken", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));

      toast.success(`Welcome back, ${result.user.displayName}!`, { autoClose: 1500 });
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error) {
      toast.error("Google login failed");
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" />

      {/* Main 2-Column Auth Card (Matching Screenshot Pixel-Neat) */}
      <div className="bg-white max-w-4xl w-full rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col md:flex-row my-6">
        
        {/* LEFT COLUMN: Lifestyle Image & Branding Overlay */}
        <div className="w-full md:w-1/2 relative bg-gray-900 overflow-hidden min-h-[350px] md:min-h-[550px] flex flex-col justify-end p-8 sm:p-10 shrink-0">
          <img
            src={authHeroImg}
            alt="Our Genie Technician"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

          {/* Overlaid Branding Text */}
          <div className="relative z-10 space-y-2 text-white">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Our Genie</h2>
            <p className="text-xs sm:text-sm text-gray-200 leading-relaxed max-w-sm">
              Professional home maintenance and concierge services at your fingertips.
            </p>
          </div>
        </div>

        {/* RIGHT COLUMN: Login Form Container */}
        <div className="w-full md:w-1/2 p-8 sm:p-10 md:p-12 flex flex-col justify-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0D004C] tracking-tight">
              Welcome Back
            </h1>
            <p className="text-xs text-gray-500 mt-1 mb-6">
              Log in to manage your home service bookings and orders.
            </p>
          </div>

          {/* Google OAuth Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full border border-gray-200 bg-white text-gray-700 font-bold text-xs py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-all shadow-sm"
          >
            <FcGoogle size={20} />
            <span>Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="relative my-6 flex items-center justify-center">
            <div className="border-t border-gray-200 w-full" />
            <span className="bg-white px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0 absolute">
              OR LOGIN WITH EMAIL / MOBILE
            </span>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email or Mobile */}
            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">Email or Mobile Number</label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="john@example.com or 9876543210"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">Password</label>
              <div className="relative">
                <FaLock className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D004C] text-white py-3.5 rounded-xl font-bold text-xs hover:bg-[#16007A] transition-all shadow-md mt-2 flex items-center justify-center gap-2"
            >
              <span>{loading ? "Logging In..." : "Log In →"}</span>
            </button>
          </form>

          {/* Don't have an account switch link */}
          <div className="mt-6 text-center text-xs text-gray-500 font-medium">
            Don't have an account?{" "}
            <Link to="/register" className="font-bold text-[#0D004C] hover:underline">
              Register
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
